import { CheckCircleIcon, CircleIcon, WarningIcon } from "@phosphor-icons/react";
import { cn } from "@reactive-resume/utils";

import {
  getBuilderSteps,
  isCustomBuilderStep,
  STEP_LABELS,
} from "@/client/constants/builder-steps";
import { debouncedUpdateResume } from "@/client/services/resume";
import { useResumeStore } from "@/client/stores/resume";

import { isStepComplete } from "../../_components/sidebar/step-status";

const COMPLETION_LABELS: Record<string, string> = {
  basics: "Coordonnées complètes",
  experience: "Expérience renseignée",
  education: "Formation ajoutée",
  skills: "Compétences renseignées",
  summary: "Profil professionnel complété",
  languages: "Langues ajoutées",
  projects: "Projets ajoutés",
  certifications: "Certifications ajoutées",
  volunteer: "Bénévolat renseigné",
  awards: "Récompenses ajoutées",
  interests: "Centres d’intérêt ajoutés",
  publications: "Publications ajoutées",
  references: "Références ajoutées",
};

export const FinalizationStep = () => {
  const resume = useResumeStore((state) => state.resume);

  const reviewedSteps = getBuilderSteps(resume.data.metadata.layout, resume.data.sections).filter(
    // NB : `getBuilderSteps` ne retourne jamais "templates" (BuilderStep),
    // la comparaison défensive historique a été retirée (TS2367).
    (step) => step !== "finalization",
  );

  const completedSteps = reviewedSteps.filter((step) => isStepComplete(step, resume));

  const progress =
    reviewedSteps.length > 0 ? Math.round((completedSteps.length / reviewedSteps.length) * 100) : 0;

  const title = String(resume.title ?? "");

  const titleMissing = !title.trim();

  const getStepName = (step: (typeof reviewedSteps)[number]) => {
    if (isCustomBuilderStep(step)) {
      return (
        resume.data.sections.custom?.[step.slice("custom.".length)]?.name ?? "Section personnalisée"
      );
    }

    return STEP_LABELS[step] ?? step;
  };

  const getCompletedLabel = (step: (typeof reviewedSteps)[number]) => {
    if (isCustomBuilderStep(step)) {
      return `${getStepName(step)} complétée`;
    }

    return COMPLETION_LABELS[step] ?? `${getStepName(step)} complété`;
  };

  const updateTitle = (nextTitle: string) => {
    const nextResume = {
      ...resume,
      title: nextTitle,
    };

    useResumeStore.setState({
      resume: nextResume,
    });

    void debouncedUpdateResume(nextResume);
  };

  return (
    <section className="mx-auto w-full max-w-[720px] pb-3">
      <p className={cn("text-[10px] font-bold uppercase", "tracking-[0.14em] text-emerald-600")}>
        Finalisation
      </p>

      <h1
        className={cn(
          "mt-1.5 text-2xl font-bold",
          "tracking-tight text-[#071451]",
          "sm:text-[30px]",
        )}
      >
        Finalisez votre CV
      </h1>

      <p className={cn("mt-1.5 max-w-xl", "text-xs leading-5 text-indigo-950/75", "sm:text-sm")}>
        Vérifiez les dernières informations avant de télécharger ou partager votre candidature.
      </p>

      <div
        className={cn(
          "mt-5 grid items-center gap-6",
          "rounded-2xl border border-indigo-100",
          "bg-white p-5",
          "sm:grid-cols-[160px_minmax(0,1fr)]",
          "sm:p-6",
        )}
      >
        <div
          role="progressbar"
          aria-label="Progression du CV"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={progress}
          className={cn(
            "relative mx-auto grid",
            "size-32 place-items-center rounded-full",
            "sm:size-36",
          )}
          style={{
            background: `conic-gradient(
                #10b981 ${progress * 3.6}deg,
                #e9e7f7 0deg
              )`,
          }}
        >
          <div
            className={cn(
              "grid size-[106px]",
              "place-items-center rounded-full bg-white",
              "text-[30px] font-bold text-[#071451]",
              "sm:size-[120px] sm:text-4xl",
            )}
          >
            {progress}%
          </div>
        </div>

        <div className="min-w-0">
          <h2 className="text-lg font-bold text-[#071451]">
            {progress === 100 ? "Votre CV est prêt" : "CV presque prêt"}
          </h2>

          <ul className="mt-3 grid gap-2.5">
            {reviewedSteps.map((step) => {
              const done = completedSteps.includes(step);

              return (
                <li
                  key={step}
                  className={cn(
                    "flex items-center gap-2.5",
                    "text-xs font-semibold text-[#071451]",
                  )}
                >
                  {done ? (
                    <CheckCircleIcon
                      aria-hidden
                      size={18}
                      weight="fill"
                      className="shrink-0 text-emerald-500"
                    />
                  ) : (
                    <CircleIcon
                      aria-hidden
                      size={18}
                      weight="regular"
                      className="shrink-0 text-slate-300"
                    />
                  )}

                  <span>{done ? getCompletedLabel(step) : `${getStepName(step)} à compléter`}</span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {titleMissing && (
        <div
          role="alert"
          className={cn(
            "mt-4 flex items-start gap-2.5",
            "rounded-xl border border-amber-200",
            "bg-amber-50 px-4 py-3",
            "text-[11px] font-medium",
            "leading-5 text-amber-700",
          )}
        >
          <WarningIcon aria-hidden size={17} className="mt-0.5 shrink-0" />
          Ajoutez un titre de CV pour faciliter son identification.
        </div>
      )}

      <label className="mt-5 block text-xs font-bold text-[#071451]">
        Titre du CV
        <input
          value={title}
          maxLength={80}
          placeholder="Ex. CV Développeur Frontend"
          onChange={(event) => {
            updateTitle(event.target.value);
          }}
          className={cn(
            "mt-2 h-11 w-full rounded-xl",
            "border border-slate-200 bg-white",
            "px-4 text-xs font-semibold",
            "outline-none transition",
            "placeholder:font-normal placeholder:text-slate-400",
            "focus:border-indigo-500 focus:ring-2",
            "focus:ring-indigo-100",
            "sm:text-sm",
          )}
        />
      </label>

      <p className="mt-1.5 text-[10px] text-slate-400">
        Ce titre vous permettra de retrouver facilement ce CV dans votre espace personnel.
      </p>
    </section>
  );
};
